import React, { useEffect } from "react";

import {
  Box,
  Hidden,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { PlusCircle } from "react-feather";
import { useSelector } from "react-redux";

import {
  Calendar,
  DesktopCalendarHeader,
  MobileCalendarHeader,
} from "@hubcapp/components/Calendar";
import { handleAddLessonClicked } from "@hubcapp/components/Calendar/components/Header/helpers";
import { selectCalendarView } from "@hubcapp/state";
import { useTealium } from "@hubcapp/context";
import Button from "@ds-developer/button";
import Content from "@ds-developer/content";
import PageHeader from "@ds-developer/page-header";

import "@ds-developer/calendar/dist/index.css";

import { CalendarView as CalendarViewType } from "@ds-developer/calendar";

import {
  DESKTOP_EXPERIMENT_BANNER_HEIGHT,
  DESKTOP_TOP_BAR_HEIGHT,
  MOBILE_TOP_BAR_HEIGHT,
} from "@hubcapp/constants";

import { useAuth } from "@hubcapp/context";
import FeatureNotAvailable from "../FeatureNotAvailable";

const titleMap: Record<CalendarViewType, string> = {
  [CalendarViewType.day]: "My day",
  [CalendarViewType.threeDay]: "My 3 days",
  [CalendarViewType.week]: "My week",
  [CalendarViewType.month]: "My month",
};

const useStyles = makeStyles(theme => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: `calc(100vh - ${
      DESKTOP_TOP_BAR_HEIGHT +
      (window?._env_?.DISPLAY_EXPERIMENT_BANNER
        ? DESKTOP_EXPERIMENT_BANNER_HEIGHT
        : 0)
    }px)`,

    [theme.breakpoints.down("xs")]: {
      minHeight: `calc(100vh - ${MOBILE_TOP_BAR_HEIGHT}px)`,
    },

    [theme.breakpoints.down("sm")]: {
      paddingBottom: "50px",
    },
  },
  content: {
    flex: "1 0 auto",
  },
  footer: {
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
    },
  },
  stickyWrapper: {
    [theme.breakpoints.down("md")]: {
      position: "fixed",
      top: "57px",
      left: "0",
      right: "0",
      backgroundColor: "#f4f6f8",
      padding: "16px 10px 0 10px",
      zIndex: 10,
    },
  },
  desktopHeaderWrapper: {
    backgroundColor: "#f4f6f8",
    [theme.breakpoints.up("sm")]: {
      position: "fixed",
      top: "125px",
      left: "10px",
      right: "10px",
      zIndex: 10,
      paddingTop: "32px",
    },
    [theme.breakpoints.up("md")]: {
      position: "initial",
      top: "auto",
      left: "auto",
      right: "auto",
      zIndex: "initial",
    },
  },
}));

const SCROLL_DELAY = 1500; // bit arbitrary but this seems to be the lowest value that works consistently

const CalendarView: React.FC = () => {
  const { instructor } = useAuth();
  if (instructor.status !== "Active") {
    return <FeatureNotAvailable />;
  }

  const classes = useStyles();
  const view = useSelector(selectCalendarView);
  const theme = useTheme();
  const { trackEvent } = useTealium();

  const isSmScreen = useMediaQuery(theme.breakpoints.down("xs"), {
    defaultMatches: false,
  });

  const scrollToCalendar = () => {
    if (isSmScreen) {
      // we seem to need a timeout here; without it,
      // something is scrolling the page back to the top
      setTimeout(
        () =>
          document
            .querySelector(
              `.fc-timegrid-slot-lane[data-time*="${
                view === CalendarViewType.day ? new Date().getHours() : "11"
              }:00:00"]`,
            )
            ?.scrollIntoView({
              block: "center",
              behavior: "smooth", // doesn't work in Safari, nor does the smoothscroll-polyfill
            }),
        SCROLL_DELAY,
      );
    }
  };
  useEffect(scrollToCalendar);

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        <Content noBottomSpacing>
          <div className={classes.stickyWrapper}>
            <PageHeader
              breadcrumbs={[
                { link: "/", title: "Home" },
                { title: "Calendar" },
              ]}
              title={titleMap[view]}
              actionButton={
                <>
                  <Hidden smUp>
                    <MobileCalendarHeader />
                  </Hidden>

                  <Hidden xsDown>
                    <Button
                      color="secondary"
                      startIcon={<PlusCircle size={14} />}
                      variant="contained"
                      onClick={() => {
                        trackEvent({
                          eventCategory: "Calendar",
                          eventAction: "Button Click",
                          eventLabel: "New Event",
                        });
                        handleAddLessonClicked();
                      }}
                    >
                      New Event
                    </Button>
                  </Hidden>
                </>
              }
            />
          </div>
          <Hidden xsDown>
            <Box mb={3} className={classes.desktopHeaderWrapper}>
              <DesktopCalendarHeader />
            </Box>
          </Hidden>
        </Content>
        <Content
          noTopSpacing
          noLeftSpacing={isSmScreen}
          noRightSpacing={isSmScreen}
          noBottomSpacing={isSmScreen}
        >
          <Calendar />
        </Content>
      </Box>
    </Box>
  );
};

export default CalendarView;
